import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { request, history, Spinner } from 'cademy-shared-web';
import { toaster } from 'evergreen-ui';
import { BookingSideSummary, Navbar, SubdomainNavbar } from '../../components/index';
import StripePaymentScreen from './StripePaymentScreen';
import ConfirmationScreen from './ConfirmationScreen';
import NonPayableScreen from './NonPayableScreen';
import styles from './CompleteBooking.module.css';
import PaymentSelectionScreen from './PaymentSelectionScreen';
import PaypalPaymentScreen from './PaypalPaymentScreen';

const CompleteBooking = ({ subdomain }) => {
    const { booking_id } = useParams();
    const [loading, setLoading] = useState(true);
    const [booking, setBooking] = useState(null);
    const [course, setCourse] = useState(null);
    const [educator, setEducator] = useState(null);
    const [occurrence, setOccurrence] = useState(null);
    const [activeScreen, setActiveScreen] = useState();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

    useEffect(() => {
        // Load the requested booking, and it's related data
        (async () => {
            try {
                const { data: bookingData } = await request.get(`/bookings/${booking_id}`);
                setBooking(bookingData);

                const {
                    data: { course, occurrences, stripe_installed, paypal_installed, educator },
                } = await request.get(
                    `/get-course-data-ids/${bookingData.educator_ref}/${bookingData.course_ref}?booking_ref=${booking_id}`
                );

                setCourse(course);
                setEducator(educator);

                const occurrence = occurrences.find(
                    (occ) => occ._id === bookingData.occurrence_ref
                );
                setOccurrence(occurrence);

                let newScreenName =
                    bookingData.payment_status === 'paid' ? 'confirmation' : 'payment';
                newScreenName = ['cancelled', 'abandoned'].includes(bookingData.payment_status)
                    ? 'non_payable'
                    : newScreenName;

                if (newScreenName === 'payment') {
                    let paymentScreen;
                    // If there is no payment apps installed default to stripe (goes to our account and we can deal with it later)
                    if (!stripe_installed && !paypal_installed) {
                        paymentScreen = 'stripe_payment';
                    } else {
                        // If both payments apps are installed, let the customer select
                        if (stripe_installed && paypal_installed) {
                            paymentScreen = 'payment_selection';
                            // If only 1 installed, display the appropriate one
                        } else {
                            if (stripe_installed) paymentScreen = 'stripe_payment';
                            if (paypal_installed) paymentScreen = 'paypal_payment';
                            setSelectedPaymentMethod(paymentScreen.split('_')[0]); // set to either stripe or paypal
                        }
                    }
                    setActiveScreen(paymentScreen);
                } else {
                    setActiveScreen(newScreenName);
                }

                setLoading(false);
            } catch (error) {
                console.error('CompleteBooking - get-data error', error);
                setLoading(false);
                toaster.danger('There was an issue loading the requested booking');
                history.push('/');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderActiveScreen = () => {
        switch (activeScreen) {
            case 'stripe_payment':
                return (
                    <StripePaymentScreen
                        booking={booking}
                        onPaymentComplete={() => setActiveScreen('confirmation')}
                    />
                );

            case 'paypal_payment':
                return (
                    <PaypalPaymentScreen
                        booking={booking}
                        onPaymentComplete={() => setActiveScreen('confirmation')}
                    />
                );

            case 'payment_selection':
                return (
                    <PaymentSelectionScreen
                        booking={booking}
                        selectedPaymentMethod={selectedPaymentMethod}
                        setSelectedPaymentMethod={setSelectedPaymentMethod}
                        nextScreen={() => setActiveScreen(`${selectedPaymentMethod}_payment`)}
                    />
                );

            case 'confirmation':
                return (
                    <ConfirmationScreen
                        subdomain={subdomain}
                        booking={booking}
                        course={course}
                        educator={educator}
                    />
                );

            case 'non_payable':
                return <NonPayableScreen subdomain={subdomain} booking={booking} course={course} />;
            // should never be needed?
            default:
                return <Spinner />;
        }
    };

    if (loading || !booking) {
        return (
            <>
                {subdomain ? <SubdomainNavbar /> : <Navbar />}
                <Spinner />
            </>
        );
    }

    // Construct a 'dummy' offersAndAddons object for the BookingSideSummary
    const offersAndAddons = { offers: booking.offers || [], add_ons: booking.add_ons || [] };

    return (
        <>
            {subdomain ? <SubdomainNavbar /> : <Navbar />}
            <div className={' container ' + styles.page_layout} style={{ marginBottom: '100px' }}>
                {renderActiveScreen()}
                <div>
                    <BookingSideSummary
                        bookingOrOrder={booking}
                        course={course}
                        occurrence={occurrence}
                        subdomain={subdomain}
                        offersAndAddons={offersAndAddons}
                        payment_method={selectedPaymentMethod}
                        educator={educator}
                    />
                </div>
            </div>
        </>
    );
};

export default CompleteBooking;
